
import * as React from "react";
import {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast";

const TOAST_LIMIT = 20;
const TOAST_REMOVE_DELAY = 1000000;

export type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  variant?: "default" | "destructive" | "success";
};

export type ToastState = {
  toasts: ToasterToast[];
};

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function generateId() {
  return (++count).toString();
}

export type Action =
  | {
      type: "ADD_TOAST";
      toast: ToasterToast;
    }
  | {
      type: "UPDATE_TOAST";
      toast: Partial<ToasterToast>;
      id: string;
    }
  | {
      type: "DISMISS_TOAST";
      id: string | undefined;
    }
  | {
      type: "REMOVE_TOAST";
      id: string | undefined;
    };

export const reducer = (state: ToastState, action: Action): ToastState => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.id ? { ...t, ...action.toast } : t
        ),
      };

    case actionTypes.DISMISS_TOAST: {
      const { id } = action;

      if (id === undefined) {
        return {
          ...state,
          toasts: state.toasts.map((t) => ({
            ...t,
            open: false,
          })),
        };
      }
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === id
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case actionTypes.REMOVE_TOAST:
      if (action.id === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.id),
      };
    default:
      return state;
  }
};

const listeners: Array<(state: ToastState) => void> = [];

let memoryState: ToastState = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, "id">;

function baseToast({ ...props }: Toast) {
  const id = generateId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      id,
      toast: props,
    });

  const dismiss = () => dispatch({ type: "DISMISS_TOAST", id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id,
    dismiss,
    update,
  };
}

function useToast() {
  const [state, setState] = React.useState<ToastState>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast: baseToast,
    dismiss: (id: string) => dispatch({ type: "DISMISS_TOAST", id }),
    dismissAll: () => dispatch({ type: "DISMISS_TOAST", id: undefined }),
  };
}

export { useToast };

export type ToastActions = ReturnType<typeof createToastActions>;

// Modified: Using module-level variable for toast actions instead of React ref
let globalToastActions: ToastActions | null = null;

export function createToastActions() {
  const actions = {
    toast: baseToast,
    dismiss: (id: string) => dispatch({ type: "DISMISS_TOAST", id }),
    dismissAll: () => dispatch({ type: "DISMISS_TOAST", id: undefined }),
  };
  
  // Update the global reference when creating new actions
  globalToastActions = actions;
  
  return actions;
}

// Helper to get current toast actions
function getToastActions(): ToastActions {
  if (!globalToastActions) {
    console.warn("Toast actions not initialized yet");
    // Return a dummy implementation
    return {
      toast: () => ({ id: "-1", dismiss: () => {}, update: () => {} }),
      dismiss: () => {},
      dismissAll: () => {},
    };
  }
  return globalToastActions;
}

// Export a simplified toast function that wraps the toast action
interface ToastFunction {
  success: (title: string, description?: string) => ReturnType<typeof baseToast>;
  error: (message: string) => ReturnType<typeof baseToast>;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export const toast: ToastFunction = {
  success: (title: string, description?: string) => {
    return getToastActions().toast({
      title,
      description,
      variant: "success",
    });
  },
  error: (message: string) => {
    return getToastActions().toast({
      title: "Error",
      description: message,
      variant: "destructive",
    });
  },
  dismiss: (id: string) => {
    getToastActions().dismiss(id);
  },
  dismissAll: () => {
    getToastActions().dismissAll();
  }
};
